import { datadogRum } from '@datadog/browser-rum';

const RELOAD_FLAG_KEY = 'chunkErrorReloadAt';
const RELOAD_DEDUPE_MS = 10_000;
const EVICT_TIMEOUT_MS = 3_000;
const FAILED_MODULE_URL = /https?:\/\/[^\s'"]+/;

const CHUNK_ERROR_PATTERNS = [
  'Failed to fetch dynamically imported module',
  'Importing a module script failed',
  'Loading chunk',
  'Loading CSS chunk',
  'error loading dynamically imported module',
];

/**
 * True when `message` matches a known stale-deploy chunk-load failure: a
 * lazy route/vendor chunk 404ing against a newer deployment (autoCodeSplitting
 * gives every route its own hashed chunk) and the host falling back to an
 * HTML response instead of the JS the browser asked for.
 */
export const isChunkError = (message: unknown): boolean => {
  if (!message || typeof message !== 'string') return false;
  return CHUNK_ERROR_PATTERNS.some(pattern => message.includes(pattern));
};

const hasRecentlyReloaded = (): boolean => {
  try {
    const last = Number(sessionStorage.getItem(RELOAD_FLAG_KEY));
    return Boolean(last) && Date.now() - last < RELOAD_DEDUPE_MS;
  } catch {
    return false;
  }
};

const markReloaded = () => {
  try {
    sessionStorage.setItem(RELOAD_FLAG_KEY, String(Date.now()));
  } catch {
    // sessionStorage unavailable (e.g. private browsing) — reload still proceeds
  }
};

/**
 * Re-fetches the chunk named in `message` past the HTTP cache. Cloudflare
 * answers a missing `/assets/*` URL with the SPA's index.html (200,
 * text/html) and `max-age=14400`, so the browser keeps that HTML under the
 * chunk's URL for 4 hours and a plain reload reads it straight back from
 * disk — every reload fails on the same chunk. `cache: 'reload'` overwrites
 * the entry with whatever the network serves now. Best effort: Safari's
 * message carries no URL, and a failed fetch still lets the reload proceed.
 */
const evictCachedChunk = async (message: unknown): Promise<void> => {
  if (typeof message !== 'string') return;
  const url = message.match(FAILED_MODULE_URL)?.[0];
  if (!url || new URL(url).origin !== window.location.origin) return;
  try {
    await fetch(url, {
      cache: 'reload',
      signal: AbortSignal.timeout(EVICT_TIMEOUT_MS),
    });
  } catch {
    // Network failure or timeout — reload anyway
  }
};

/**
 * Reloads once to pick up the new deployment's index.html/chunk hashes,
 * first evicting the failed chunk named in `message` from the HTTP cache.
 * Returns `false` (and skips the reload) if one already fired within
 * `RELOAD_DEDUPE_MS`, so callers with a fallback UI (e.g. the root
 * ErrorBoundary) can show it instead of looping reloads forever.
 */
export const recoverFromChunkError = (message?: unknown): boolean => {
  if (hasRecentlyReloaded()) return false;
  markReloaded();
  void evictCachedChunk(message).finally(() => window.location.reload());
  return true;
};

const toError = (reason: unknown): Error => {
  if (reason instanceof Error) return reason;
  return new Error(typeof reason === 'string' ? reason : 'Unhandled rejection');
};

/**
 * Global safety net for chunk-load failures that never reach the router/React
 * tree (e.g. a rejected prefetch outside of render). Route-level failures
 * that surface through render are instead caught by the root ErrorBoundary
 * (see `components/shared/ErrorBoundary`). Everything else is forwarded to
 * Datadog RUM so it still counts as a real error in monitoring.
 */
export const setupChunkErrorRecovery = () => {
  window.addEventListener('unhandledrejection', event => {
    const message = event.reason?.message || String(event.reason || '');
    if (isChunkError(message)) {
      recoverFromChunkError(message);
      return;
    }
    datadogRum.addError(toError(event.reason), {
      source: 'unhandledrejection',
    });
  });

  window.addEventListener('error', event => {
    if (isChunkError(event.message)) {
      recoverFromChunkError(event.message);
      return;
    }
    datadogRum.addError(
      event.error ?? new Error(event.message || 'Unknown error'),
      {
        source: 'window.error',
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      },
    );
  });
};
